import React, { useEffect, useState } from "react"
import { Card, Spin, Alert } from "antd"
import { fetchLoanOverviewV2 } from "./api"
import ApplicantsCardV2, {
  LoanCardV2,
  MobileHomeCardV2,
  IncomeCardV2,
  AssetsCardV2,
  ExpensesCardV2,
  applicantsTitleV2,
} from "./cards"
import "./OverviewV2.css"

function CardBody({ loading, error, children }) {
  if (loading) {
    return (
      <div className="overview-v2-card-loading">
        <Spin size="small" />
      </div>
    )
  }
  if (error) {
    return <Alert type="warning" message={error} showIcon />
  }
  return children
}

/**
 * Split-table overview sections (V2).
 * Card shells render immediately; each body shows Spin until overview loads.
 * @param {string} loanId
 * @param {(ready: boolean) => void} [onReadyChange] called when overview request idle state changes
 */
export default function OverviewSplitSections({ loanId, onReadyChange }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [data, setData] = useState(null)

  useEffect(() => {
    let cancelled = false
    if (!loanId) {
      setLoading(false)
      setError("missing loan id")
      return
    }
    setLoading(true)
    setError("")
    const token = sessionStorage.getItem("ZeitroA")
    fetchLoanOverviewV2(loanId, token)
      .then((js) => {
        if (!cancelled) {
          setData(js)
          setLoading(false)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || "Failed to load overview")
          setLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [loanId])

  useEffect(() => {
    if (typeof onReadyChange === "function") {
      onReadyChange(!loading)
    }
  }, [loading, onReadyChange])

  const applicants = data?.Applicants || []
  const loan = data?.Loan || null
  const income = data?.Income || []
  const assets = data?.Assets || {}
  const expenses = data?.Expenses || {}
  const showMobileHome =
    !loading && !error && loan?.propertyType === "mobilehome"

  return (
    <div className="overview-v2-split">
      <div className="mt-3 text-wrap">
        <Card type="inner" title={applicantsTitleV2(applicants)}>
          <CardBody loading={loading} error={error}>
            <ApplicantsCardV2 applicants={applicants} />
          </CardBody>
        </Card>
      </div>

      <div className="mt-5 text-wrap">
        <Card type="inner" title="Loan">
          <CardBody loading={loading} error={error}>
            <LoanCardV2 loan={loan} />
          </CardBody>
        </Card>
      </div>

      {showMobileHome && (
        <div className="mt-5 text-wrap">
          <Card type="inner" title="Manufactured home info">
            <CardBody loading={loading} error={error}>
              <MobileHomeCardV2 loan={loan} />
            </CardBody>
          </Card>
        </div>
      )}

      <div className="mt-5 text-wrap">
        <Card type="inner" title="Income">
          <CardBody loading={loading} error={error}>
            <IncomeCardV2 income={income} />
          </CardBody>
        </Card>
      </div>

      <div className="mt-5 text-wrap">
        <Card type="inner" title="Assets">
          <CardBody loading={loading} error={error}>
            <AssetsCardV2 assets={assets} />
          </CardBody>
        </Card>
      </div>

      <div className="mt-5 text-wrap">
        <Card type="inner" title="Expenses">
          <CardBody loading={loading} error={error}>
            <ExpensesCardV2 expenses={expenses} />
          </CardBody>
        </Card>
      </div>
    </div>
  )
}
